import { Button, PreviewPanel, type PreviewPanelLabels, useToast } from "@design";
import { Download, ExternalLink } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type CollectionFileSummary, errorMessage } from "@/api";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { saveBlob } from "@/lib/download";
import { useViewport } from "@/responsive/viewport";
import { FilePreviewContent, type FilePreviewTab } from "./FilePreviewContent";
import { type FileView, fileViewOf, type KnowledgeFile } from "./filePreview";

function usePanelLabels(listLabel: string): PreviewPanelLabels {
  const { t } = useTranslation("common", { keyPrefix: "filePreview.panel" });
  return {
    outputs: listLabel,
    close: t("close"),
    previous: t("previous"),
    next: t("next"),
    position: (current, total) => t("position", { current, total }),
    view: t("view"),
    expand: t("expand"),
    collapse: t("collapse"),
    resize: t("resize"),
  };
}

function useFileActions() {
  const { t } = useTranslation("common", { keyPrefix: "filePreview" });
  const toast = useToast();
  return {
    download: async (file: KnowledgeFile, filename: string) => {
      try {
        const original = await file.original();
        saveBlob(original.blob, original.filename ?? filename);
      } catch (error) {
        toast.error(errorMessage(error, t("downloadFailed")));
      }
    },
    /** Text and extracted text open as plain text, never run; documents open as their PDF. */
    openInNewTab: async (file: KnowledgeFile, view: FileView) => {
      const tab = window.open("", "_blank");
      try {
        let blob: Blob;
        if (view.kind === "pages") blob = (await file.pdf()).blob;
        else if (view.kind === "image") blob = (await file.original()).blob;
        else if (view.kind === "text") blob = new Blob([(await file.original()).blob], { type: "text/plain" });
        else blob = new Blob([await file.markdown()], { type: "text/plain" });
        if (tab) tab.location.href = URL.createObjectURL(blob);
      } catch (error) {
        tab?.close();
        toast.error(errorMessage(error, t("openFailed")));
      }
    },
  };
}

/**
 * Side panel previewing knowledge-base files the way chat shows documents: PDF and Office files as
 * pages, Markdown/CSV/HTML rendered, images as-is, anything else as its extracted text. Remount it
 * (key) when the file changes to reset the tab.
 */
export function FilePreviewPanel({
  files,
  fileUuid,
  fileFor,
  listLabel,
  onOpen,
  onClose,
}: Readonly<{
  files: readonly CollectionFileSummary[];
  fileUuid: string;
  fileFor: (file: CollectionFileSummary) => KnowledgeFile;
  /** Names the list the panel switches between (e.g. "Files in this data source"). */
  listLabel: string;
  onOpen: (fileUuid: string) => void;
  onClose: () => void;
}>) {
  const { t } = useTranslation("common", { keyPrefix: "filePreview" });
  const labels = usePanelLabels(listLabel);
  const actions = useFileActions();
  const { isMobile } = useViewport();
  const [tab, setTab] = useState<FilePreviewTab>("preview");
  const index = files.findIndex((f) => f.uuid === fileUuid);
  const summary = files[index];
  if (!summary) return null;
  const view = fileViewOf(summary);
  const file = fileFor(summary);
  const tabs: { value: FilePreviewTab; label: string }[] = [{ value: "preview", label: t("tabs.preview") }];
  if (view.kind === "text") tabs.push({ value: "source", label: t("tabs.source") });
  if (view.kind !== "extracted")
    tabs.push({ value: "extracted", label: t(isMobile ? "tabs.extractedShort" : "tabs.extracted") });

  return (
    <PreviewPanel
      defaultWidth={560}
      maxWidth={760}
      defaultExpanded={isMobile}
      labels={labels}
      artifacts={files.map((f) => ({ key: f.uuid, name: f.filename }))}
      activeIndex={index}
      onSelectArtifact={(i) => onOpen(files[i].uuid)}
      tabs={tabs}
      activeTab={tab}
      onTabChange={(next) => setTab(next as FilePreviewTab)}
      onClose={onClose}
      actions={
        <>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            leadingIcon={<ExternalLink size={14} />}
            onClick={() => void actions.openInNewTab(file, tab === "extracted" ? { kind: "extracted" } : view)}
          >
            {t("openInNewTab")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            fullWidth
            leadingIcon={<Download size={14} />}
            onClick={() => void actions.download(file, summary.filename)}
          >
            {t("download")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Text variant="caption" tone="tertiary">
          {t("added", { date: formatDateTime(summary.createdAt) })}
        </Text>
        <FilePreviewContent file={file} view={view} tab={tab} title={summary.filename} />
      </div>
    </PreviewPanel>
  );
}
